import { Dialog, DialogProps, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from 'assets/closeIcon.svg';
import { formatResumeFilename } from 'modules/hiring/utilities';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { colors } from 'styles/colors';
import styles from './ResumeDialog.module.scss';
import ResumeDialogSkeleton from './ResumeDialogSkeleton';
// eslint-disable-next-line import/no-extraneous-dependencies
import FileViewer from 'react-file-viewer';
import DocxPreview from './DocxPreview';

interface IResumeDialogProps {
  candidateName: string;
  resumeFileName: string;
  open: boolean;
  onClose: () => void;
  docLink: string | undefined;
  fileType: any;
  showLoader: boolean;
  resume: any;
}

const isPdfFile = (fileType: string | undefined, fileName: string) =>
  fileType?.toLowerCase() === 'pdf' || fileName.toLowerCase().endsWith('.pdf');

const useStyles = makeStyles({
  paper: {
    maxHeight: 'calc(100vh - 1.25rem)',
    width: '53.25rem',
    border: `0.0625rem solid ${colors.border.mediumGrey}`,
    borderRadius: '0.5rem',
    boxShadow: `0.25rem 0.5rem 1rem ${colors.blackPearl}32`,
    overflow: 'hidden',
    margin: 0,
  },
  dialogBody: {
    overflow: 'auto',
    '& .pg-viewer-wrapper': {
      overflow: 'hidden !important',
    },
    '& .pg-viewer-wrapper .document-container': {
      width: 'auto !important',
    },
    '& .pg-viewer-wrapper canvas': {
      paddingLeft: '1.75rem',
    },
    '&::-webkit-scrollbar': {
      height: '1.25rem',
      width: '0.5rem',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: colors.background.darkGray,
      borderRadius: '62.4375rem',
      height: '6.25rem',
      width: '0.5rem',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: colors.scrollBar.hover,
    },
  },
  hidePdfControls: {
    '& #pdf-controls': {
      display: 'none',
    },
  },
});

const ResumeDialog = ({
  candidateName,
  resumeFileName,
  open,
  onClose,
  docLink,
  fileType,
  showLoader,
  resume,
}: IResumeDialogProps) => {
  const [scroll] = React.useState<DialogProps['scroll']>('paper');
  const [docURI, setDocURI] = React.useState<string>('');
  const [dialogEntered, setDialogEntered] = useState(false);
  const classes = useStyles();
  const isPdf = isPdfFile(fileType, resumeFileName);

  useLayoutEffect(() => {
    if (isPdf) {
      setDocURI(docLink ?? '');
    } else {
      setDocURI('');
    }
    return () => {
      setDocURI('');
    };
  }, [isPdf, docLink]);

  useEffect(() => {
    if (!open) {
      setDialogEntered(false);
      return;
    }

    const frame = requestAnimationFrame(() => setDialogEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const canPreview = open && dialogEntered && !showLoader && !!docLink;
  const showPdfPreview = canPreview && isPdf;
  const showDocxPreview = canPreview && !isPdf;

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      aria-labelledby='simple-dialog-title'
      open={open}
      scroll={scroll}
    >
      {showLoader ? (
        <ResumeDialogSkeleton />
      ) : (
        <>
          <Grid container justifyContent='space-between' className={styles.dialogHeader}>
            <Grid item xs={4}>
              <a href={docLink} download={formatResumeFilename(resumeFileName)}>
                <TypographyWithI18N
                  id={`download_${resumeFileName}`}
                  data-testid={`download_${resumeFileName}`}
                  className={`kfic2-bodyTextLink ${styles.downloadLink}`}
                  aria-disabled={!docLink}
                  i18nKey='download'
                />
              </a>
            </Grid>
            <Grid item xs={4} className={styles.resumeName}>
              <TypographyWithI18N
                id='resumeTitle'
                className={`kfic2-metadata ${styles.heading}`}
                data-testid='resumeTitle'
                i18nKey='resumeOf'
              />
              <Typography
                id='resumeCandidateName'
                data-testid='resumeCandidateName'
                className='kfic2-smallHeaderBold'
                title={candidateName}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {candidateName}
              </Typography>
            </Grid>
            <Grid item xs={4} justifyContent='flex-end' className={styles.closeIcon} id='closeIcon'>
              <img
                src={CloseIcon}
                title='close'
                alt='icon'
                onClick={onClose}
                id='closeIcon'
                data-testid='closeIcon'
                width='16px'
                height='16px'
              />
            </Grid>
          </Grid>
          <Grid
            className={`${styles.dialogBody} ${classes.dialogBody}`}
            data-testid='fileViewer'
            id='fileViewer'
          >
            {docLink ? (
              <>
                {showPdfPreview ? (
                  <FileViewer key={docLink} fileType='pdf' filePath={docLink} />
                ) : showDocxPreview ? (
                  <DocxPreview docLink={docLink} rawResume={resume} />
                ) : null}
              </>
            ) : (
              <Grid item xs={12} className={styles.noData}>
                <TypographyWithI18N
                  id='noDataAvailable'
                  data-testid='noDataAvailable'
                  className='kfic2-bodyText'
                  i18nKey='noResumeAvailable'
                  textSuffix='.'
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Dialog>
  );
};

export default ResumeDialog;
